import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { ReactComponent as NotificationsIcon } from 'assets/icons/bell.svg';
import { ReactComponent as LogoIcon } from 'assets/icons/logo.svg';
import { ReactComponent as TaskManagementIcon } from 'assets/icons/task.svg';
import { ReactComponent as ProfileIcon } from 'assets/icons/user.svg';
import Button from 'components/Button/Button';
import LinkButton from 'components/LinkButton/LinkButton';
import NotificationsModal from 'components/NotificationsModal/NotificationsModal';
import Separator from 'components/Separator/Separator';
import Toggle from 'components/Toggle/Toggle';
import { AppRoutes } from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import {
  BUTTON_VARIANT, SEPARATOR_VARIANT, THEME, USER_PILLAR_ACCESS_TYPES, USERS_ROLE,
} from 'constants/interfaces';
import { useAiChatPanel } from 'contexts/AiChatPanelContext';
import useTheme from 'hooks/useTheme';
import { useAppSelector } from 'store/hooks/useApp';
import { authTokensSelector } from 'store/slices/auth/selectors';
import {
  useGetCountUnreadNotificationsQuery, useGetNotificationsQuery,
  useReadAllNotificationsMutation, useReadOneNotificationMutation,
} from 'store/slices/notifications/apis/notificationsApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getAccessTaskManagement from 'utils/getAccessTaskManagement';

import type INotification from 'store/slices/notifications/interfaces/INotification';
import type IUser from 'store/slices/user/interfaces/IUser';

import styles from './sass/Header.module.scss';

function Header(): ReactElement {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { accessToken } = useAppSelector(authTokensSelector);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const queryParams = accessToken ? undefined : skipToken;
  const { data: user } = useUserMeQuery(queryParams, { refetchOnMountOrArgChange: true });
  const [status, setStatus] = useState('');
  const [notificationsOffset, setNotificationsOffset] = useState(0);
  const [allNotifications, setAllNotifications] = useState<INotification[]>([]);

  const companyName = user?.data?.company?.name;
  const { companyLogo, partnerLogo } = user?.data?.company?.uiConfiguration || {};
  const isSuperAdmin = user?.data?.role === USERS_ROLE.admin;
  const userParams = getAccessTaskManagement(user?.data as IUser);
  const noAccessTaskManagement = userParams.permissionTaskManagement === USER_PILLAR_ACCESS_TYPES.noAccess
  || userParams.permissionTaskManagement === USER_PILLAR_ACCESS_TYPES.invisibleDisabled
  || userParams.permissionTaskManagement === USER_PILLAR_ACCESS_TYPES.dimmedDisabled;
  const { theme, onToggleTheme } = useTheme();
  const { togglePanel } = useAiChatPanel();

  const {
    data: notifications,
    isFetching: isNotificationsLoading,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery({
    offset: notificationsOffset,
    status,
    limit: DATA_SET_PAGINATION_COUNT,
  });

  useEffect(() => {
    refetchNotifications();
  }, [refetchNotifications, status]);

  const [readAllNotifications] = useReadAllNotificationsMutation();
  const [readOneNotification] = useReadOneNotificationMutation();
  const {
    data: countUnreadNotifications,
    isFetching: isCountUnreadNotificationsLoading,
    refetch: refetchCountUnreadNotifications,
  } = useGetCountUnreadNotificationsQuery();

  const handleNotificationsModalOpen = () => {
    setIsNotificationsModalOpen(!isNotificationsModalOpen);
  };

  const handleMarkAllAsRead = () => {
    readAllNotifications().then(() => {
      refetchCountUnreadNotifications();
    });
  };

  const handleReadOneNotification = (notificationId: number) => {
    readOneNotification({ notificationId }).then(() => {
      refetchCountUnreadNotifications();
    });
  };

  const handleRedirectToTask = (taskId: number) => {
    navigation(`${AppRoutes.taskManagement}/${taskId}`);
  };

  const handleGetNextNotification = () => {
    setNotificationsOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
  };

  const handleStatusChange = (statusTab: string) => {
    setStatus(statusTab);
    setNotificationsOffset(0);
  };

  const existUnreadNotifications = (countUnreadNotifications?.data?.count ?? 0) > 0;

  useEffect(() => {
    if (notifications?.data?.items && notificationsOffset === 0) {
      setAllNotifications(notifications.data.items);
    } else if (notifications?.data?.items && notificationsOffset > 0) {
      setAllNotifications((prev) => [...prev, ...notifications.data.items]);
    }
  }, [notifications, notificationsOffset]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.logoContainer}>
          {partnerLogo ? (
            <Link to="/">
              <div
                className={styles.partnerLogo}
                style={{ background: `url(${partnerLogo}) no-repeat left / contain` }}
              />
            </Link>
          ) : (
            <Link to="/">
              <LogoIcon />
            </Link>
          )}
          <Separator className={styles.separator} position={SEPARATOR_VARIANT.vertical} />
          {companyLogo || companyName ? (
            <Link
              to="/"
              className={styles.companyLink}
              aria-label={companyName || t('home.pageTitle')}
              title={companyName}
            >
              {companyLogo ? (
                <img
                  className={styles.companyLogo}
                  src={companyLogo}
                  alt=""
                />
              ) : null}
              {companyName ? <span className={styles.companyName}>{companyName}</span> : null}
            </Link>
          ) : null}
        </div>

        <div className={styles.navigation}>
          <Button
            title={t('aiChat.openPanel')}
            startIcon={<AiIcon />}
            onClick={togglePanel}
            variant={BUTTON_VARIANT.secondary}
          />
          {!noAccessTaskManagement && user?.data?.role !== USERS_ROLE.admin ? (
            <LinkButton
              to={AppRoutes.taskManagement}
              title={t('common.taskManagement')}
              startIcon={<TaskManagementIcon />}
            />
          ) : null}
          {isSuperAdmin ? (
            <label className={styles.themeToggle} htmlFor="header-theme-toggle">
              <span>{t('common.darkMode')}</span>
              <Toggle
                name="header-theme-toggle"
                value="dark"
                checked={theme === THEME.dark}
                onChange={() => onToggleTheme()}
              />
            </label>
          ) : null}
          <div className={styles.notificationsButtonWrapper}>
            <Button
              title={t('common.notifications')}
              startIcon={<NotificationsIcon />}
              onClick={handleNotificationsModalOpen}
              variant={BUTTON_VARIANT.ghost}
              className={styles.notificationsButton}
            />
            {existUnreadNotifications ? (
              <div className={styles.unreadNotificationsDot} />
            ) : null}
          </div>
          <LinkButton to={AppRoutes.profile} title={t('profile.title')} startIcon={<ProfileIcon />} />
        </div>

      </header>
      <NotificationsModal
        open={isNotificationsModalOpen}
        onClose={handleNotificationsModalOpen}
        onMarkAllAsRead={handleMarkAllAsRead}
        onReadOneNotification={handleReadOneNotification}
        notifications={allNotifications}
        onGetNextNotification={handleGetNextNotification}
        onStatusChange={handleStatusChange}
        isLoading={isNotificationsLoading || isCountUnreadNotificationsLoading}
        hasNextPage={notifications?.data?.meta?.hasNext ?? false}
        handleRedirectToTask={handleRedirectToTask}
      />
    </>
  );
}

export default Header;
